import { Box, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AgGrid from "../../../components/grid/AgGrid";
import { defaultColDef, leftAlignColDef } from "../../../components/grid/columnDefinitions/columnDefinitions";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import { ProductPassportVisitLog } from "../../../db_access/product_passport_visit_log";

/**
 * Read-only, paginated log of QR-code scans on this passport's public
 * hosting link — each row is a visit recorded by DPP_View_Published.js
 * (device type/OS via UA parsing, approximate location via IP lookup).
 * A scan happens on a completely separate device/session, so there's no
 * live push signal for new rows — refetches on entityId change and on the
 * app's shared `refresh` toggle (the same one RefreshButton flips elsewhere),
 * so a manual refresh click updates this without a full page reload.
 */
const VisitLogGrid = ({ entityId, entityType = "product" }) => {
  const { t } = useTranslation();
  const { refresh } = useContextProvider();
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!entityId) {
      setRows([]);
      return;
    }
    ProductPassportVisitLog.get_by_entity_id(entityId, entityType)
      .then((res) => res.json())
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]));
  }, [entityId, entityType, refresh]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: t("Scanned At"),
        field: "timestamp",
        ...leftAlignColDef,
        ...defaultColDef,
        minWidth: 170,
        flex: 1.2,
        valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleString() : "-"),
      },
      { headerName: t("Country"), field: "country_name", ...leftAlignColDef, ...defaultColDef, minWidth: 140, flex: 1 },
      { headerName: t("Region"), field: "region", ...leftAlignColDef, ...defaultColDef, minWidth: 140, flex: 1 },
      { headerName: t("City"), field: "city", ...leftAlignColDef, ...defaultColDef, minWidth: 140, flex: 1 },
      { headerName: t("Device Type"), field: "device_type", ...leftAlignColDef, ...defaultColDef, minWidth: 120, flex: 1 },
      { headerName: t("Device OS"), field: "device_os", ...leftAlignColDef, ...defaultColDef, minWidth: 120, flex: 1 },
    ],
    [t]
  );

  const rowData = useMemo(() => rows.map((r) => ({ ...r, __key: r.id })), [rows]);

  if (!rowData.length) {
    return <Typography sx={{ fontSize: "13px", color: "#94a3b8", fontFamily: "Poppins, sans-serif", p: 2 }}>{t("No scans recorded yet.")}</Typography>;
  }

  return (
    <Box sx={{ width: 1 }}>
      <AgGrid
        rowData={rowData}
        columnDefs={columnDefs}
        getRowId={(params) => String(params.data.__key)}
        pagination={true}
        domLayout="autoHeight"
      />
    </Box>
  );
};
export default VisitLogGrid;
